import type { LoggingOverviewDateRange } from '@/api/hooks/useIntegrationLogs'
import { lastDays } from '@/utils/dateRanges'
import { DatePicker, Tooltip } from 'antd'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

/**
 * The window both logs pages open on. Mirrors the trailing week
 * useLoggingOverview falls back to, but as whole calendar days -- so the dates
 * the picker shows are the dates actually queried, and the end covers the rest
 * of today rather than stopping at the moment of page load.
 */
export function defaultLogsRange(): LoggingOverviewDateRange {
  const [from, to] = lastDays(7)
  return { from: from.toDate(), to: to.toDate() }
}

interface LogsRangePickerProps {
  value: LoggingOverviewDateRange | undefined
  onChange: (range: LoggingOverviewDateRange | undefined) => void
  /** Present when something else has taken the window out of play -- disables
   * the picker and says why, rather than showing dates that are not applied. */
  disabledReason?: string
}

/**
 * The window both integration logs pages report over. Dates only: nobody
 * filters logs to the hour, and a time picker made the common case (a few
 * whole days) four fields of work instead of two.
 */
export function LogsRangePicker({
  value,
  onChange,
  disabledReason,
}: LogsRangePickerProps) {
  const picker = (
    <RangePicker
      disabled={!!disabledReason}
      value={value ? [dayjs(value.from), dayjs(value.to)] : null}
      // A date means the whole of that date at both ends, so picking today
      // includes what has happened so far today rather than nothing at all.
      onChange={(dates) =>
        onChange(
          dates?.[0] && dates[1]
            ? {
                from: dates[0].startOf('day').toDate(),
                to: dates[1].endOf('day').toDate(),
              }
            : undefined,
        )
      }
      format='YYYY-MM-DD'
      presets={[
        { label: 'Today', value: lastDays(1) },
        { label: 'Last 7 Days', value: lastDays(7) },
        { label: 'Last 2 Weeks', value: lastDays(14) },
        { label: 'Last 30 Days', value: lastDays(30) },
      ]}
    />
  )
  return disabledReason ? (
    <Tooltip title={disabledReason}>{picker}</Tooltip>
  ) : (
    picker
  )
}
